// CLIPBOARDUL editorului: copierea unui element sau a proprietăților lui și
// lipirea lor — funcții pure pe geometrie, fără ecran.
//
// Două copieri, fiindcă omul vrea două lucruri: «încă unul ca ăsta» (ELEMENTUL:
// un ESV nou, cu secțiunile lui pe fiecare nivel, pus unde dă clic) și «și
// ăla să fie ca ăsta» (PROPRIETĂȚILE: secțiunea scrisă pe un element care
// există). Același clipboard le ține pe amândouă: ce s-a copiat e un element,
// iar lipirea hotărăște ce ia din el.
//
// O grindă n-are «loc» fără două capete, deci un element-grindă nu se lipește
// pe plan: lipirea lui intră în unealta Grindă cu secțiunea gata scrisă
// (`EditorStructura`), iar primele două clicuri o trag. Aici e doar secțiunea.
//
// Clipboardul NU e al sistemului (nimeni n-are ce face cu un ESV în altă
// aplicație): stă în interfață, în `sessionStorage`, ca să treacă de o
// reîncărcare și de la un corp la altul.

import {
  adaugaEsv, redimEsv, redimGrinda,
  type Geometrie, type Pt, type SectiuneEsv, type SectiuneGrinda,
} from "./editorGeometrie";

export type Clipboard =
  | { fel: "esv"; sursa: string; axa: "X" | "Y"; ofs_mm: number;
      sectiune_per_nivel: Record<string, SectiuneEsv> }
  | { fel: "grinda"; sursa: string; sectiune_per_nivel: Record<string, SectiuneGrinda> };

export const CHEIE_CLIPBOARD = "ob.editor-structura.clipboard";

/** Ce se copiază de pe elementul `id`: tot ce e al secțiunii, nimic din
 *  loc (nodul, capetele grinzii, baza). */
export function copie(G: Geometrie, id: string): Clipboard | null {
  const e = G.esv.find((x) => x.id === id);
  if (e) {
    const sec: Record<string, SectiuneEsv> = {};
    for (const [n, s] of Object.entries(e.sectiune_per_nivel)) {
      const { dim_extra: _x, ...rest } = s;
      void _x;
      sec[n] = { ...rest, brate: s.brate?.map((b) => ({ ...b })) };
      if (!sec[n].brate?.length) delete sec[n].brate;
    }
    return { fel: "esv", sursa: id, axa: e.axa, ofs_mm: e.ofs_mm ?? 0, sectiune_per_nivel: sec };
  }
  const g = G.grinzi.find((x) => x.id === id);
  if (g) {
    const sec: Record<string, SectiuneGrinda> = {};
    for (const [n, s] of Object.entries(g.sectiune_per_nivel)) sec[n] = { b_cm: s.b_cm, h_m: s.h_m };
    return { fel: "grinda", sursa: id, sectiune_per_nivel: sec };
  }
  return null;
}

/** Secțiunea grinzii copiate la `nivel` — sau cea de la nivelul ei cel mai
 *  apropiat, când grinda n-a existat pe nivelul ăsta. */
export function sectiuneaGrinziiCopiate(c: Clipboard, nivel: number): SectiuneGrinda | null {
  if (c.fel !== "grinda") return null;
  const niveluri = Object.keys(c.sectiune_per_nivel).map(Number);
  if (!niveluri.length) return null;
  const cel = niveluri.reduce((a, n) => (Math.abs(n - nivel) < Math.abs(a - nivel) ? n : a));
  const s = c.sectiune_per_nivel[String(cel)];
  return { b_cm: s.b_cm, h_m: s.h_m };
}

/** Lipirea unui ESV copiat, cu nodul în `nod` (deja lipit de zid și de axe)
 *  și pe aceleași niveluri pe care le avea sursa, tăiate la `niveluriCorp`.
 *  Un element fără brațe se întoarce pe axa zidului în care a căzut (`axa`),
 *  ca unealta ESV; cu brațe își ține axa, fiindcă brațele sunt desenate în
 *  cadrul lui. */
export function lipesteEsv(G: Geometrie, c: Clipboard, nod: Pt, niveluriCorp: number[],
                           axa: "X" | "Y" | null): { G: Geometrie; id: string } | null {
  if (c.fel !== "esv") return null;
  const niveluri = Object.keys(c.sectiune_per_nivel).map(Number)
    .filter((n) => niveluriCorp.includes(n)).sort((a, b) => a - b);
  if (!niveluri.length) return null;
  const jos = c.sectiune_per_nivel[String(niveluri[0])];
  const areBrate = niveluri.some((n) => (c.sectiune_per_nivel[String(n)].brate ?? []).length > 0);
  const r = adaugaEsv(G, { nod, axa: areBrate || !axa ? c.axa : axa,
                           b_cm: jos.b_cm, L_cm: jos.L_cm, niveluri });
  const sec: Record<string, SectiuneEsv> = {};
  for (const n of niveluri) {
    const s = c.sectiune_per_nivel[String(n)];
    sec[String(n)] = { ...s, brate: s.brate?.map((b) => ({ ...b })) };
    if (!sec[String(n)].brate?.length) delete sec[String(n)].brate;
  }
  return {
    G: { ...r.G, esv: r.G.esv.map((e) => (e.id === r.id
      ? { ...e, ofs_mm: c.ofs_mm, sectiune_per_nivel: sec } : e)) },
    id: r.id,
  };
}

/** Lipirea PROPRIETĂȚILOR pe elementul `id`, de aceeași familie: la ESV
 *  secțiunea (b, L) și — pe aceeași axă — alunecarea și brațele; la grindă
 *  b × h. `nivel` = numai nivelul ăsta, null = toate nivelurile elementului
 *  (regula lui `redimEsv`). Sursa de la care se ia secțiunea e nivelul
 *  `nivelRef` al copiei, sau cel mai apropiat al ei. */
export function lipesteProprietati(G: Geometrie, c: Clipboard, id: string, nivelRef: number,
                                   nivel: number | null): Geometrie | null {
  const e = G.esv.find((x) => x.id === id);
  if (e) {
    if (c.fel !== "esv") return null;
    const s = ceaMaiApropiata(c.sectiune_per_nivel, nivelRef);
    if (!s) return null;
    let out = redimEsv(G, id, nivel, { b_cm: s.b_cm, L_cm: s.L_cm });
    if (e.axa !== c.axa) return out;
    // aceeași axă: brațele și alunecarea sunt în același cadru, deci se iau
    // ca atare — pe nivelurile pe care elementul le are
    const brate = s.brate?.map((b) => ({ ...b }));
    out = {
      ...out,
      esv: out.esv.map((x) => {
        if (x.id !== id) return x;
        const sec: Record<string, SectiuneEsv> = {};
        for (const [n, v] of Object.entries(x.sectiune_per_nivel)) {
          if (nivel != null && Number(n) !== nivel) { sec[n] = v; continue; }
          const { brate: _b, ...rest } = v;
          void _b;
          sec[n] = brate?.length ? { ...rest, ofs_mm: s.ofs_mm ?? 0, brate: brate.map((b) => ({ ...b })) }
            : { ...rest, ofs_mm: s.ofs_mm ?? 0 };
        }
        return { ...x, sectiune_per_nivel: sec };
      }),
    };
    return out;
  }
  const g = G.grinzi.find((x) => x.id === id);
  if (g) {
    if (c.fel !== "grinda") return null;
    const s = ceaMaiApropiata(c.sectiune_per_nivel, nivelRef);
    return s ? redimGrinda(G, id, nivel, { b_cm: s.b_cm, h_m: s.h_m }) : null;
  }
  return null;
}

function ceaMaiApropiata<S>(sec: Record<string, S>, nivel: number): S | null {
  const niveluri = Object.keys(sec).map(Number);
  if (!niveluri.length) return null;
  const cel = niveluri.reduce((a, n) => (Math.abs(n - nivel) < Math.abs(a - nivel) ? n : a));
  return sec[String(cel)];
}

/** Lipirea se poate face pe `id`? — aceeași familie ca ce e în clipboard. */
export function sePoateLipiPe(G: Geometrie, c: Clipboard | null, id: string): boolean {
  if (!c) return false;
  if (G.esv.some((x) => x.id === id)) return c.fel === "esv";
  if (G.grinzi.some((x) => x.id === id)) return c.fel === "grinda";
  return false;
}

export function citesteClipboardul(): Clipboard | null {
  try {
    const c = JSON.parse(sessionStorage.getItem(CHEIE_CLIPBOARD) ?? "null");
    return c && (c.fel === "esv" || c.fel === "grinda") ? c : null;
  } catch {
    return null;
  }
}

export function tineClipboardul(c: Clipboard | null): void {
  try {
    if (c) sessionStorage.setItem(CHEIE_CLIPBOARD, JSON.stringify(c));
    else sessionStorage.removeItem(CHEIE_CLIPBOARD);
  } catch { /* fără sessionStorage: clipboardul ține cât pagina */ }
}

/** Numele a ce e în clipboard, pentru meniu și cipul uneltei: «ST7 · 30×140». */
export function numeleCopiei(c: Clipboard, nivel: number): string {
  if (c.fel === "esv") {
    const s = ceaMaiApropiata(c.sectiune_per_nivel, nivel);
    return s ? `${c.sursa} · ${s.b_cm}×${s.L_cm}` : c.sursa;
  }
  const s = sectiuneaGrinziiCopiate(c, nivel);
  return s ? `${c.sursa} · ${s.b_cm}×${Math.round(s.h_m * 100)}` : c.sursa;
}

